import React, { useState, useEffect } from 'react';
import { shuffle } from '../../utils/array';
import type { CognitiveBias } from '../../types';
import { GradientTitle, AccentButton } from '../ui';
import { ScoreTableau } from '../ui/ScoreTableau';
import { MarkdownContent } from '../ui/MarkdownContent';
import { Sparkles, X, BookOpen, FileText } from 'lucide-react';
import { Portal } from '../ui/Portal';

interface BiasMatchGameProps {
  biases: CognitiveBias[];
}

export const BiasMatchGame: React.FC<BiasMatchGameProps> = ({ biases }) => {
  const [selectedBiases, setSelectedBiases] = useState<CognitiveBias[]>([]);
  const [examples, setExamples] = useState<Array<{ biasId: string; text: string }>>([]);
  const [selectedExample, setSelectedExample] = useState<string | null>(null);
  const [selectedBias, setSelectedBias] = useState<string | null>(null);
  const [matches, setMatches] = useState<Set<string>>(new Set());
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [isGameComplete, setIsGameComplete] = useState(false);
  const [matchedBiases, setMatchedBiases] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [usedExamples, setUsedExamples] = useState<Set<string>>(new Set());
  const [contentType, setContentType] = useState<'definition' | 'example'>('example');
  
  // Session statistics
  const [roundNumber, setRoundNumber] = useState(1);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [correctMatches, setCorrectMatches] = useState(0);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [streak, setStreak] = useState(0);
  const [accuracy, setAccuracy] = useState(0);

  // Effect to calculate accuracy whenever attempts or correct matches change
  useEffect(() => {
    if (totalAttempts > 0) {
      setAccuracy(Math.round((correctMatches / totalAttempts) * 100));
    } else {
      setAccuracy(0);
    }
  }, [totalAttempts, correctMatches]);

  // Handle mode switching more robustly
  const switchMode = (newMode: 'definition' | 'example') => {
    if (isProcessing || isGameComplete || newMode === contentType) return;
    
    console.log(`Switching mode from ${contentType} to ${newMode}`);
    
    // First reset the game state
    setMatches(new Set());
    setMatchedBiases(new Set());
    setSelectedExample(null);
    setSelectedBias(null);
    setIsCorrect(null);
    setIsGameComplete(false);
    setUsedExamples(new Set());
    setRoundNumber(1);
    setTotalAttempts(0);
    setCorrectMatches(0);
    setIncorrectAttempts(0);
    setStreak(0);
    setAccuracy(0);
    
    // Then update the content type
    setContentType(newMode);
    
    // After state is updated, initialize the game with new content
    // Use setTimeout to ensure state updates have completed
    setTimeout(() => {
      const biasesWithContent = newMode === 'definition'
        ? biases.filter(bias => bias.short_description)
        : biases.filter(bias => bias.examples && Array.isArray(bias.examples) && bias.examples.length > 0);
      
      if (biasesWithContent.length > 0) {
        const count = Math.min(3, biasesWithContent.length);
        const randomBiases = shuffle(biasesWithContent).slice(0, count);
        setSelectedBiases(randomBiases);
        
        // Process biases based on the new mode
        const selectedContent = randomBiases.map(bias => {
          if (newMode === 'definition') {
            return {
              biasId: bias.id,
              text: bias.short_description
            };
          } else {
            // For example mode, find a suitable example
            const anyExample = bias.examples![0];
            return {
              biasId: bias.id,
              text: anyExample.description
            };
          }
        });
        
        console.log(`Setting content for mode: ${newMode}`);
        setExamples(shuffle(selectedContent));
      }
    }, 0);
  };

  // Effect to force rerender when contentType changes
  useEffect(() => {
    console.log(`Content type changed to: ${contentType}`);
  }, [contentType]);

  // Initialize game with 3 random biases and their examples or definitions
  const initializeGame = (isFirstRound: boolean = false) => {
    // Make sure we're using the latest contentType
    const currentMode = contentType;
    console.log(`Initializing game with contentType: ${currentMode}`);
    
    // Select biases depending on the source type
    let biasesWithContent;
    
    // If definition mode is selected, we need biases with definitions
    // If example mode is selected, we need biases with examples
    if (currentMode === 'definition') {
      biasesWithContent = biases.filter(bias => bias.short_description);
    } else {
      // For example mode, we need biases with examples
      biasesWithContent = biases.filter(bias => 
        bias.examples && 
        Array.isArray(bias.examples) && 
        bias.examples.length > 0
      );
    }

    if (biasesWithContent.length > 0) {
      const count = Math.min(3, biasesWithContent.length);
      const randomBiases = shuffle(biasesWithContent).slice(0, count);
      setSelectedBiases(randomBiases);

      // For each bias, get the appropriate content based on source type
      const selectedContent = randomBiases.map(bias => {
        if (currentMode === 'definition') {
          // For definition mode, use the short_description
          return {
            biasId: bias.id,
            text: bias.short_description
          };
        } else {
          // For example mode, use an example
          const biasExamples = bias.examples!.map((ex, index) => ({
            biasId: bias.id,
            text: ex.description,
            key: `${bias.id}-${index}`
          })).filter(ex => !usedExamples.has(ex.key));

          // If no unused examples for this bias, use any example
          if (biasExamples.length === 0) {
            const anyExample = bias.examples![0];
            return {
              biasId: bias.id,
              text: anyExample.description
            };
          }

          // Take a random unused example
          const selectedExample = shuffle(biasExamples)[0];
          
          // Mark the example as used
          setUsedExamples(prev => new Set([...prev, selectedExample.key]));

          return {
            biasId: selectedExample.biasId,
            text: selectedExample.text
          };
        }
      });
      
      console.log(`Setting examples/definitions for mode: ${currentMode}`);
      setExamples(shuffle(selectedContent));
    }
  };

  // Reset game state for source change
  const resetGame = () => {
    setMatches(new Set());
    setMatchedBiases(new Set());
    setSelectedExample(null);
    setSelectedBias(null);
    setIsCorrect(null);
    setIsGameComplete(false);
    setUsedExamples(new Set());
    setRoundNumber(1);
    setTotalAttempts(0);
    setCorrectMatches(0);
    setIncorrectAttempts(0);
    setStreak(0);
    setAccuracy(0);
  };

  // Initial game setup
  useEffect(() => {
    initializeGame(true);
  }, [biases]);

  const handleExampleClick = (example: string) => {
    if (matches.has(example) || isProcessing) return;
    
    setSelectedExample(example);

    if (selectedBias) {
      setIsProcessing(true);
      const matchingExample = examples.find(e => e.text === example);
      if (!matchingExample) return;

      setTotalAttempts(prev => prev + 1);

      if (matchingExample.biasId === selectedBias) {
        const newMatches = new Set([...matches, example]);
        setMatches(newMatches);
        setMatchedBiases(new Set([...matchedBiases, selectedBias]));
        setIsCorrect(true);
        setCorrectMatches(prev => prev + 1);
        setStreak(prev => prev + 1);
        
        if (newMatches.size === examples.length) {
          setIsGameComplete(true);
        }
      } else {
        setIsCorrect(false);
        setIncorrectAttempts(prev => prev + 1);
        setStreak(0);
      }

      setTimeout(() => {
        setSelectedExample(null);
        setSelectedBias(null);
        setIsCorrect(null);
        setIsProcessing(false);
      }, 600);
    }
  };

  const handleBiasClick = (biasId: string) => {
    if (matches.has(selectedExample || '') || isProcessing) return;
    
    setSelectedBias(biasId);

    if (selectedExample) {
      setIsProcessing(true);
      const example = examples.find(e => e.text === selectedExample);
      if (!example) return;

      setTotalAttempts(prev => prev + 1);

      if (example.biasId === biasId) {
        const newMatches = new Set([...matches, selectedExample]);
        setMatches(newMatches);
        setMatchedBiases(new Set([...matchedBiases, biasId]));
        setIsCorrect(true);
        setCorrectMatches(prev => prev + 1);
        
        if (newMatches.size === examples.length) {
          setIsGameComplete(true);
        }
      } else {
        setIsCorrect(false);
        setIncorrectAttempts(prev => prev + 1);
      }

      setTimeout(() => {
        setSelectedExample(null);
        setSelectedBias(null);
        setIsCorrect(null);
        setIsProcessing(false);
      }, 600);
    }
  };

  const startNewRound = () => {
    setRoundNumber(prev => prev + 1);
    setIsGameComplete(false);
    setMatches(new Set());
    setMatchedBiases(new Set());
    setSelectedExample(null);
    setSelectedBias(null);
    setIsCorrect(null);
    
    initializeGame();
  };

  if (selectedBiases.length === 0) {
    return (
      <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100">
        <GradientTitle className="text-2xl mb-6">No examples available</GradientTitle>
        <p>Please try again later when more examples are added.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100">
        {/* Score Tableau */}
        <div className="mb-8">
          <ScoreTableau
            totalAttempts={totalAttempts}
            correctMatches={correctMatches}
            accuracy={accuracy}
            streak={streak}
          />
        </div>
        
        <GradientTitle 
          className="text-2xl md:text-3xl text-center mb-6" 
          useH1={false}
          key={`title-${contentType}`}
        >
          Match {contentType === 'definition' ? 'Definitions' : 'Examples'} with Their Biases
        </GradientTitle>
        
        {/* Source selector */}
        <div className="flex justify-center mb-12">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            <button
              onClick={() => {
                console.log("Definition button clicked");
                switchMode('definition');
              }}
              disabled={isProcessing || isGameComplete}
              className={`flex items-center gap-2 px-5 py-3 transition-all duration-300 ${
                contentType === 'definition' 
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <BookOpen size={18} />
              Definitions
            </button>
            <button
              onClick={() => {
                console.log("Example button clicked");
                switchMode('example');
              }}
              disabled={isProcessing || isGameComplete}
              className={`flex items-center gap-2 px-5 py-3 transition-all duration-300 ${
                contentType === 'example' 
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <FileText size={18} />
              Examples
            </button>
          </div>
        </div>

        <div className="space-y-12 max-w-4xl mx-auto">
          {/* Biases */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">
              Cognitive Biases:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {selectedBiases.map((bias) => (
                <button
                  key={`bias-${bias.id}`}
                  onClick={() => handleBiasClick(bias.id)}
                  disabled={matchedBiases.has(bias.id) || isProcessing}
                  className={`p-6 rounded-2xl text-center transition-all duration-300 font-medium text-lg
                    ${matchedBiases.has(bias.id)
                      ? 'bg-green-50 text-green-700 border-2 border-green-200 shadow-green-100 scale-[0.98]'
                      : selectedBias === bias.id 
                        ? isCorrect === true
                          ? 'bg-green-50 border-2 border-green-200 shadow-green-100 scale-[0.98]'
                          : isCorrect === false
                          ? 'bg-red-50 border-2 border-red-200 shadow-red-100 scale-[0.98]'
                          : 'bg-gradient-to-r from-orange-50 to-pink-50 border-2 border-orange-300 shadow-lg scale-[1.02]'
                        : 'hover:bg-gradient-to-r hover:from-orange-50/50 hover:to-pink-50/50 border border-gray-200 hover:border-orange-200 hover:shadow-md hover:scale-[1.01]'
                    } disabled:cursor-not-allowed`}
                >
                  {bias.name}
                </button>
              ))}
            </div>
          </div>

          {/* Examples/Definitions section */}
          <div className="space-y-6">
            <h3 
              className="text-xl font-semibold text-gray-900"
              key={`section-title-${contentType}`}
            >
              {contentType === 'definition' ? 'Definitions' : 'Examples'} to Match:
            </h3>
            <div className="space-y-4">
              {examples.map((example) => (
                <button
                  key={`example-${example.biasId}-${example.text}`}
                  onClick={() => handleExampleClick(example.text)}
                  disabled={matches.has(example.text) || isProcessing}
                  className={`w-full p-6 rounded-2xl text-left transition-all duration-300 text-lg
                    ${matches.has(example.text) 
                      ? 'bg-green-50 text-green-700 border-2 border-green-200 shadow-green-100 scale-[0.98]'
                      : selectedExample === example.text
                      ? 'bg-gradient-to-r from-orange-50 to-pink-50 border-2 border-orange-300 shadow-lg scale-[1.02]'
                      : 'hover:bg-gradient-to-r hover:from-orange-50/50 hover:to-pink-50/50 border border-gray-200 hover:border-orange-200 hover:shadow-md hover:scale-[1.01]'
                    } disabled:cursor-not-allowed disabled:opacity-75`}
                >
                  <MarkdownContent content={example.text} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Congratulations Modal - Use Portal and match ShareModal style */}
      {isGameComplete && (
        <Portal>
          <div 
            className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" 
            onClick={startNewRound} // Close on backdrop click
            role="dialog"
            aria-modal="true"
            aria-labelledby="congrats-modal-title"
          >
            <div 
              className="bg-white rounded-xl shadow-xl max-w-md w-full animate-fadeIn" 
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
              style={{ animationDuration: '0.2s' }}
            >
              {/* Modal Header (similar to ShareModal, but just title) */}
              <div className="flex items-center justify-center p-4 pt-6 border-b border-gray-100 relative">
                <h3 id="congrats-modal-title" className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-orange-500" />
                  Congratulations!
                </h3>
                {/* Close Button */}
                <button
                  onClick={startNewRound}
                  className="absolute right-3 top-3 p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                  aria-label="Close congratulations modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-6 text-center">
                <div className="space-y-6">
                  <p className="text-gray-600">
                    You've successfully matched all {contentType === 'definition' ? 'definitions' : 'examples'} with their cognitive biases!
                  </p>
                  
                  {/* Round Stats - Simplified layout */}
                  <div className="grid grid-cols-2 gap-4 py-4 border-t border-b border-gray-100">
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">Accuracy</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {accuracy}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">Matches</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {correctMatches}
                      </div>
                    </div>
                  </div>

                  <AccentButton 
                    onClick={startNewRound}
                    className="w-full justify-center py-3"
                  >
                    Play Next Round
                  </AccentButton>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};